/** Display timezone for analytics charts (EST/EDT). Storage remains UTC. */
export const ANALYTICS_DISPLAY_TIME_ZONE = 'America/New_York';

function getZonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const get = (type) => {
    const value = parts.find((part) => part.type === type)?.value;
    return value ? Number(value) : NaN;
  };

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

/** YYYY-MM-DD in America/New_York for a UTC instant. */
export function toDisplayDate(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function addDisplayCalendarDays(isoDate, days) {
  const cursor = new Date(`${isoDate}T12:00:00.000Z`);
  cursor.setUTCDate(cursor.getUTCDate() + days);
  return cursor.toISOString().slice(0, 10);
}

/** UTC instant for YYYY-MM-DD 00:00:00 in America/New_York. */
export function startOfDisplayDayUtc(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  let utcMs = Date.UTC(year, month - 1, day, 0, 0, 0);

  for (let i = 0; i < 4; i += 1) {
    const parts = getZonedParts(new Date(utcMs), ANALYTICS_DISPLAY_TIME_ZONE);
    const asIfUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    const desired = Date.UTC(year, month - 1, day, 0, 0, 0);
    const diff = desired - asIfUtc;
    if (diff === 0) break;
    utcMs += diff;
  }

  return new Date(utcMs);
}

export function endOfDisplayDayExclusiveUtc(isoDate) {
  return startOfDisplayDayUtc(addDisplayCalendarDays(isoDate, 1));
}

export function countVisitorsOnDisplayDate(sessionTimestamps, isoDate) {
  const startMs = startOfDisplayDayUtc(isoDate).getTime();
  const endMs = endOfDisplayDayExclusiveUtc(isoDate).getTime();
  let count = 0;
  for (const ts of sessionTimestamps) {
    const ms = Date.parse(ts);
    if (!Number.isNaN(ms) && ms >= startMs && ms < endMs) count += 1;
  }
  return count;
}

/** Build one row per Eastern calendar day in [fromDate, toDate]. */
export function buildDailyVisitorCounts(sessionTimestamps, fromDate, toDate) {
  const countByDate = new Map();
  for (const ts of sessionTimestamps) {
    const ms = Date.parse(ts);
    if (Number.isNaN(ms)) continue;
    const day = toDisplayDate(new Date(ms));
    countByDate.set(day, (countByDate.get(day) ?? 0) + 1);
  }

  const daily = [];
  let cursor = fromDate;
  while (cursor <= toDate) {
    daily.push({
      date: cursor,
      count: countByDate.get(cursor) ?? 0,
    });
    cursor = addDisplayCalendarDays(cursor, 1);
  }
  return daily;
}
